import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { List } from 'immutable';

import { resultsConfigSelector, featureCountSelector } from 'ctm-selectors';

import { getFilterStaged } from '../utils';
import { Checkbox } from '../../form';
import { Text } from '../../text/text';
import { withRules } from '../../../higher_order_components/with_rules';

import styles from './checkbox_list_filter.css';

const objValues = require('lodash.values');

/**
 * Checkboxes in filters can be selected by default or deactivated by default, depending on their type.
 * E.G. By default, all brands should be selected, but not all product features.
 * If you then select a feature, it will only show products that have that feature.
 * @returns {CheckboxListFilter} A checkbox list filter component
 */
class _CheckboxListFilter extends Component {

    /**
     * Determine if the component should re-render based on the nextProps
     * @param  {Object} nextProps  The incoming set of props
     * @returns {Boolean} true if the component should re-render
     */
    shouldComponentUpdate = (nextProps) => {
        const { selectedValues } = this.props;
        return selectedValues.size !== nextProps.selectedValues.size;
    };

    /**
     * Generates Checkbox components with a specific schema that is to be used across all
     * filters that are lists of checkboxes.
     * Options are a list of keys and values.
     * @returns {Array<Checkbox>} An array of checkboxes
     */
    generateCheckboxes = () => {
        const {
            filterProps,
            featureCounts,
            selectedValues,
            dispatch
        } = this.props;
        const { filter, fetchNewResults, options = [], showFeatureCounts = false } = filterProps;
        const filterStaged = getFilterStaged(filterProps);
        return options.map((item) => {
            const code = item.code;
            if (!code || (showFeatureCounts && !featureCounts[code])) return null;
            const identifier = `filters.${filter}.${code}`;
            const selectedValuesList = selectedValues || List();
            const value = selectedValuesList.includes(code) ? code : '';

            const checkedActions = [
                {
                    type: 'SET_FILTER_ARRAY',
                    filter,
                    filterStaged,
                    filterValue: code
                }
            ];
            const uncheckedActions = [
                {
                    type: 'REMOVE_FILTER_ARRAY',
                    filter,
                    filterStaged,
                    filterValue: code
                }
            ];
            if (fetchNewResults) {
                const fetchNewResultsAction = {
                    type: 'SET_RESULTS_CONFIG_VALUE',
                    key: 'filters.fetchNewResults',
                    value: true
                };
                if (item.formStateKey) {
                    const newFilterVar = code.replace(/[/.]+/g, '');
                    dispatch({
                        type: 'SET_FILTER_VALUE',
                        filter: newFilterVar,
                        filterStaged,
                        filterValue: !!value
                    });
                    dispatch({
                        type: 'SET_FILTER_ACTION',
                        resultsStateKey: `filters.${newFilterVar}`,
                        resultsStagedStateKey: `helpers.filters.${newFilterVar}`,
                        formStateKey: item.formStateKey
                    });
                }
                checkedActions.push(fetchNewResultsAction);
                uncheckedActions.push(fetchNewResultsAction);
            }

            if (filterStaged) {
                const updateButtonAction = {
                    type: 'SET_RESULTS_CONFIG_VALUE',
                    key: 'filters.showFiltersUpdateButton',
                    value: true
                };
                checkedActions.push(updateButtonAction);
                uncheckedActions.push(updateButtonAction);
            }
            const label = showFeatureCounts ? `${item.label} (${featureCounts[code]})` : `${item.label}`;
            const schema = {
                field: {
                    type: 'checkbox',
                    UIComponent: 'checkbox_medium_rounded',
                    value: code,
                    label
                },
                identifier,
                stateKey: 'notRequired',
                eventProps: {
                    onChange: {
                        checked: {
                            actions: checkedActions
                        },
                        unchecked: {
                            actions: uncheckedActions
                        }
                    }
                }
            };

            return (
                <Checkbox
                    className={styles.root}
                    key={schema.identifier}
                    schema={schema}
                    value={value}
                    dispatch={dispatch}
                    touched={false}
                    nonJourneyField
                />
            );
        });
    };

    /**
     * Return a list of checkboxes for filters.
     * @returns {Node} The checkbox components
     */
    render = () => {
        const { featureCounts, title } = this.props;
        if (featureCounts && objValues(featureCounts).every((option) => option === 0)) {
            return null;
        }
        return (
            <>
                {title && <Text size="large" color="brand_base_120" label={title} />}
                {this.generateCheckboxes()}
            </>
        );
    };

}

_CheckboxListFilter.propTypes = {
    title: PropTypes.string,
    filterProps: PropTypes.shape({
        // append to this, or change to string, if naming new filters
        filter: PropTypes.oneOf(['brands', 'features']),
        showUpdateButton: PropTypes.bool,
        fetchNewResults: PropTypes.bool,
        showFeatureCounts: PropTypes.bool,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                label: PropTypes.string,
                formStateKey: PropTypes.string
            })
        ).isRequired
    }).isRequired,
    showFeatureCounts: PropTypes.bool,
    featureCounts: PropTypes.object,
    selectedValues: PropTypes.object,
    dispatch: PropTypes.func
};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} state - The entire application state
 * @param {Object} props - The props for the form group this is being applied to
 * @returns {Object}     - Props for the component, mapped from state values
 */
const mapStateToProps = (state, props) => {
    const { filter } = props.filterProps;
    const statePosition = ['filters', filter];
    if (getFilterStaged(props.filterProps)) {
        statePosition.unshift('helpers');
    }
    const selectedValues = resultsConfigSelector(state).getIn(statePosition) || List();

    let featureCounts = props.featureCounts;

    if (!featureCounts && props.filterProps.showFeatureCounts) {
        featureCounts = featureCountSelector(state, props);
    }
    return {
        featureCounts,
        selectedValues
    };
};

export { _CheckboxListFilter };
export const CheckboxListFilter = withRules(connect(mapStateToProps)(_CheckboxListFilter));
